import type { AiMatch } from "../api/ai.api";
import type { ChatLang } from "../hooks/use-ai-chat";
import { parseDoc, parseInline, type DocBlock } from "../lib/doc-markdown";
import { DocProse, renderInline } from "./doc-prose";
import { MatchGrid } from "./match-card";
import { SOFT } from "./soft-tokens";

type Section = {
  heading: string | null;
  blocks: DocBlock[];
  /** Slugs this section names, in prose order, deduped, catalogue-backed. */
  slugs: string[];
};

/**
 * The planning answer, read as an index into the catalogue.
 *
 * Phone ruling (answer-sections.tsx): every `##` section owns the rows its own
 * prose names, so the order of the cards is the order of the sentences — the
 * thing a single flat row of cards under the whole answer cannot express.
 *
 * Chat kit C4 + C5: each section's cards sit on their own sideways rail under
 * its prose, and the names are bold navy words that read as part of the
 * sentence.
 */
export function AnswerSections({
  text,
  matches,
  lang,
  inTrip,
  onAdd,
  numbers,
}: {
  text: string;
  matches: AiMatch[];
  lang: ChatLang;
  inTrip: Set<string>;
  onAdd: (id: string) => void;
  /** Slug to pin number, forwarded to the cards so card 7 is pin 7. */
  numbers?: ReadonlyMap<string, number>;
}) {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  const sections = buildSections(text, bySlug);

  // Only headed sections carry a number; the lead-in paragraph has none.
  let dayNumber = 0;

  return (
    <div>
      {sections.map((section, si) => {
        if (section.heading !== null) dayNumber += 1;
        const sectionMatches = section.slugs.map((s) => bySlug.get(s)!).filter(Boolean);
        return (
          <div key={si}>
            {section.heading !== null ? (
              <div className="mb-2.5 mt-5 flex items-center gap-2.5">
                <span
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold tabular-nums"
                  style={{ background: SOFT.inkGradient, color: SOFT.gold }}
                >
                  {dayNumber}
                </span>
                <h4 className="min-w-0 flex-1 text-start text-[15px] font-semibold" style={{ color: SOFT.ink }}>
                  {renderInline(section.heading, bySlug)}
                </h4>
              </div>
            ) : null}

            {section.blocks.length > 0 ? (
              <DocProse
                text={section.blocks
                  .map((b) => (b.kind === "bullet" ? `- ${b.text}` : b.text))
                  .join("\n")}
                matches={matches}
              />
            ) : null}

            {sectionMatches.length > 0 ? (
              <div className="mt-3">
                <MatchGrid
                  matches={sectionMatches}
                  lang={lang}
                  inTrip={inTrip}
                  onAdd={onAdd}
                  numbers={numbers}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function buildSections(text: string, bySlug: Map<string, AiMatch>): Section[] {
  const out: Section[] = [];
  let current: Section = { heading: null, blocks: [], slugs: [] };

  for (const block of parseDoc(text)) {
    if (block.kind === "heading") {
      out.push(current);
      current = { heading: block.text, blocks: [], slugs: [] };
      continue;
    }
    current.blocks.push(block);
    for (const node of parseInline(block.text)) {
      if (node.kind !== "entity") continue;
      if (current.slugs.includes(node.slug)) continue;
      if (!bySlug.has(node.slug)) continue;
      current.slugs.push(node.slug);
    }
  }
  out.push(current);

  // The lead-in is empty when the document opens straight on a heading.
  return out.filter((s) => s.heading !== null || s.blocks.length > 0);
}

/**
 * Rows the sectioned answer never named, so they are shown once at the end
 * rather than dropped. Retrieval routinely returns more than the prose uses.
 */
export function unnamedMatches(text: string, matches: AiMatch[]): AiMatch[] {
  const bySlug = new Map(matches.map((m) => [m.slug, m]));
  const used = new Set(buildSections(text, bySlug).flatMap((s) => s.slugs));
  return matches.filter((m) => !used.has(m.slug));
}
